<template>
  <section class="form-list detail-container" v-loading="isLoading">
    <el-card shadow="never" class="detail-card">
      <div slot="header" class="detail-header">
        <div class="detail-header-left">
          <span class="detail-title">操作日志 #{{ log.id }}</span>
          <el-tag size="small">{{ actionName }}</el-tag>
          <el-tag size="small" :type="targetTypeTag">{{ log.target_type_name }}</el-tag>
        </div>
        <div class="detail-header-right">
          <el-button v-if="targetLink" type="primary" size="small" icon="el-icon-view" @click="goTarget">
            查看{{ log.target_type_name }}
          </el-button>
          <el-button size="small" icon="el-icon-back" @click="back">返回</el-button>
        </div>
      </div>

      <div v-if="log.id" class="detail-main info-grid">
        <div class="apply-info-row">
          <span class="info-label">操作人</span>
          <span class="info-value">
            <router-link v-if="operatorLink" :to="operatorLink" class="primary-color">{{ log.operator_nickname }}</router-link>
            <span v-else>{{ log.operator_nickname || `#${log.operator_id}` }}</span>
          </span>
        </div>
        <div class="apply-info-row">
          <span class="info-label">操作对象</span>
          <span class="info-value target-value">
            <router-link v-if="targetLink" :to="targetLink" class="primary-color">{{ targetDisplayName }}</router-link>
            <span v-else class="dead-text">{{ targetDisplayName }}</span>
            <el-tag v-if="!log.target_exists" size="mini" type="info">已失效</el-tag>
            <span v-if="log.target_desc" class="target-desc">{{ log.target_desc }}</span>
          </span>
        </div>
        <div class="apply-info-row">
          <span class="info-label">操作时间</span>
          <span class="info-value">{{ log.add_time | timeToYmdHi }}</span>
        </div>
        <div class="apply-info-row">
          <span class="info-label">IP</span>
          <span class="info-value">{{ log.ip || '未记录' }}</span>
        </div>
        <div v-if="log.user_agent" class="apply-info-row info-row-wide">
          <span class="info-label">浏览器</span>
          <span class="info-value user-agent">{{ log.user_agent }}</span>
        </div>
      </div>
      <div v-else-if="!isLoading" class="empty-text">未找到该操作日志</div>
    </el-card>

    <el-card v-if="log.id" shadow="never" class="detail-card">
      <div slot="header" class="detail-header">
        <div class="detail-header-left">
          <span class="detail-title">变更明细</span>
          <span v-if="changes.length" class="count-tip">共 {{ changes.length }} 项，按字段所属的表分组</span>
          <span v-else class="count-tip">本次操作没有字段变更</span>
        </div>
      </div>

      <!-- 字段中文名、ID→名称、金额/时间格式、字段归属表全部由后端算好，前端只做排版。 -->

      <!-- 分组维度是「这个字段属于哪张表」：管理员域里 6 张表互相有同名字段（store_id / apply_state / remark…），
           光看中文标签判断不出改的是哪条记录。表名只在组头出现一次，不再每行重复挂标签。 -->
      <div v-if="isResultMode && changes.length" class="mode-tip">
        本次操作的前后不是同一条记录：标「原记录」的是操作前已有的数据，标「本次写入」的是本次操作写入的数据，两者不构成新旧对比。
      </div>

      <div v-if="changes.length" class="change-groups">
        <div v-for="group in changeGroups" :key="group.key" class="change-group">
          <div class="group-header">
            <span class="group-title">{{ group.title }}</span>
            <span v-if="group.table" class="group-table">{{ group.table }}</span>
            <span class="group-count">{{ group.list.length }} 项</span>
          </div>
          <div class="change-rows">
            <div
              v-for="change in group.list"
              :key="change.field"
              class="change-row"
              :class="{ 'change-row-wide': isWideRow(change) }"
              :title="`${group.title}｜${change.label}（${columnName(change)}）`"
            >
              <span class="row-label">{{ change.label }}</span>
              <span class="row-values">
                <!-- 类型标签固定占一列（同组内没有标签的行也留位），值才不会一行缩进、一行顶格 -->
                <span v-if="group.hasKind" class="row-kind">
                  <el-tag v-if="change.kind !== 'update'" size="mini" :type="kindTag(change)">{{ kindName(change) }}</el-tag>
                </span>
                <!-- 只有「修改」才是新旧对比（旧值划掉 + 新值加粗）；其余只有一侧有值，不画箭头 -->
                <template v-if="change.kind === 'update'">
                  <span class="side-value side-old side-gone">{{ change.from }}</span>
                  <span class="row-arrow">→</span>
                  <span class="side-value side-new">{{ change.to }}</span>
                </template>
                <span v-else class="side-value" :class="valueClass(change)">{{ singleValue(change) }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </el-card>
  </section>
</template>

<script>
import RouteConfig from '@/router/config';
import { operationLogShow } from '@/api/personnel';
import { findOperationAction } from '@/enums/operationActions';
import { findOperationTargetType, findOperationChangeKind } from '@/enums/operationLogs';

export default {
  name: RouteConfig.administratorOperationLogDetail.name,
  data: () => ({
    isLoading: false,
    log: {},
  }),
  computed: {
    changes() {
      return this.log.changes || [];
    },
    // change_mode 由后端判定：result = 前后快照不是同一个对象
    isResultMode() {
      return this.log.change_mode === 'result';
    },
    // 按「字段属于哪张表」分组（table / table_name 由后端解析下发），组顺序与组内顺序都沿用后端返回顺序。
    // 老后端没有这两个字段时全部归到「未标注归属表」一组，不会渲染成空标题。
    changeGroups() {
      const groups = [];
      const indexOfTable = {};
      this.changes.forEach((change) => {
        const key = change.table || change.table_name || 'unknown';
        if (indexOfTable[key] === undefined) {
          indexOfTable[key] = groups.length;
          groups.push({
            key,
            title: change.table_name || '未标注归属表',
            table: change.table || '',
            list: [],
          });
        }
        groups[indexOfTable[key]].list.push(change);
      });
      // 同一张表里既有「修改」（无标签）又有「新增/移除/本次写入」（有标签）时，
      // 给标签留出固定一列，值才不会一行缩进、一行顶格
      groups.forEach((group) => {
        group.hasKind = group.list.some((change) => change.kind !== 'update');
      });
      return groups;
    },
    actionName() {
      return findOperationAction(this.log.action || '').name;
    },
    targetTypeTag() {
      return findOperationTargetType(this.log.target_type).tagType;
    },
    targetDisplayName() {
      return this.log.target_name || `#${this.log.target_id}`;
    },
    operatorLink() {
      return this.log.operator_id ? RouteConfig.administratorEdit.to(this.log.operator_id) : '';
    },
    targetLink() {
      // 对象已被物理删除（任职记录）或账号已注销时不给跳转，避免点进 404
      if (!this.log.target_exists) return '';
      const linkKey = findOperationTargetType(this.log.target_type).linkKey;
      const page = linkKey && RouteConfig[linkKey];
      return page && page.to ? page.to(this.log.target_id) : '';
    },
  },
  watch: {
    '$route'(to) {
      this.load(to);
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    async load(route = this.$route) {
      this.isLoading = true;
      const { isError, data } = await operationLogShow(route.params.id);
      this.isLoading = false;
      if (isError) return;
      this.log = data || {};
    },
    kindName(change) {
      return findOperationChangeKind(change.kind).name;
    },
    kindTag(change) {
      return findOperationChangeKind(change.kind).tagType;
    },
    // 字段全名：带表名的 column 路径（操作结果类字段不是表字段，只留字段名）
    columnName(change) {
      return change.table ? `${change.table}.${change.field}` : change.field;
    },
    // 这一行的值放得下几列：值太长（≈ 两行以上）就别塞在三列窄规格里，改成独占整行。
    // 中文按 1 个字宽、半角（英文/数字/标点）按 0.5 个估算，只看展示宽度不看语义。
    isWideRow(change) {
      const text = change.kind === 'update'
        ? `${change.from || ''}${change.to || ''}`
        : (this.singleValue(change) || '');

      let width = 0;
      for (const char of String(text)) width += char.charCodeAt(0) <= 0xff ? 0.5 : 1;
      return width > 26;
    },
    // 只有一侧有值的行：移除/原记录用旧值色，新增/本次写入用新值色
    // （行首的 kind 标签已经说明来历，不必再像旧版那样在值上面加一行「写入值/原记录值」小标题）
    valueClass(change) {
      return change.kind === 'delete' || change.kind === 'source' ? 'side-old' : 'side-new';
    },
    singleValue(change) {
      return change.kind === 'create' || change.kind === 'result' ? change.to : (change.from || change.to);
    },
    goTarget() {
      this.$router.push(this.targetLink);
    },
    back() {
      RouteConfig.administratorOperationLogDetail.back();
    },
  },
};
</script>

<style scoped>
/* 详情页靠左：.form-list 已负责容器宽度与内边距，这里只限宽，绝不居中 */
.detail-container {
  max-width: 1200px;
}

.detail-card {
  margin-bottom: 16px;
}

.detail-main {
  font-size: 14px;
  line-height: 1.9;
}

/* 基本信息两列起排：操作人/操作对象 一行、操作时间/IP 一行，小值不再各占整行；
   窄屏自动回落单列。只有 user_agent 这种长文本单独占一行。 */
.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  column-gap: 28px;
}

.info-row-wide {
  grid-column: 1 / -1;
}

.detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.detail-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.detail-header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.detail-title {
  font-size: 16px;
  font-weight: bold;
  color: #303133;
}

.dead-text {
  color: #f56c6c;
}

/* 操作对象：名字 / 已失效标记 / 门店·职位说明之间统一用 gap 留间距 */
.target-value {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.target-desc {
  color: #606266;
  font-size: 13px;
}

.count-tip {
  color: #606266;
  font-size: 13px;
  font-weight: normal;
}

/* 结果模式（前后不是同一对象）的说明条：橙字压浅橙底要够深才看得清 */
.mode-tip {
  margin-bottom: 12px;
  padding: 8px 12px;
  background: #fdf6ec;
  border: 1px solid #faecd8;
  border-radius: 4px;
  color: #b88230;
  font-size: 13px;
  line-height: 1.6;
}

.change-group {
  overflow: hidden;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
}

/* 组头：中文表名 + 物理表名（给开发排查留痕）+ 这张表本次被改了几个字段 */
.group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-bottom: 1px solid #ebeef5;
}

.group-title {
  color: #303133;
  font-size: 14px;
  font-weight: bold;
}

.group-table {
  padding: 0 6px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  background: #fff;
  color: #606266;
  font-size: 12px;
  font-family: Consolas, Monaco, 'Courier New', monospace;
}

.group-count {
  margin-left: auto;
  color: #606266;
  font-size: 12px;
}

.user-agent {
  word-break: break-all;
}

.apply-info-row {
  display: flex;
  align-items: baseline;
  padding: 10px 0;
  border-bottom: 1px solid #f5f7fa;
}

.apply-info-row:last-child {
  border-bottom: none;
}

.info-label {
  flex-shrink: 0;
  width: 90px;
  text-align: right;
  color: #606266;
  margin-right: 16px;
}

.info-value {
  flex: 1;
  min-width: 0;
  word-break: break-all;
}

.empty-text {
  color: #909399;
  text-align: center;
  padding: 40px 0;
}

.change-groups {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* 一条变更一行；值通常很短，容器够宽时按 360px 起自动排两/三列，不再一个字段占满整行 */
.change-rows {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  column-gap: 28px;
  padding: 6px 12px 10px;
}

.change-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 4px 0;
  line-height: 1.6;
}

/* 长文案（企业微信返回信息、结果说明）单独占满整行宽度：
   挤在窄列里时它会把「本次写入」标签和值推成上下两行，读起来像断成了两句 */
.change-row-wide {
  grid-column: 1 / -1;
}

.row-label {
  /* 132px = 最长中文标签「企业微信绑定结果」8 字（≈112px）+ 余量。
     原来写 112px 正好卡在那条线上，最后一个「果」被挤到第二行。
     字典里没登记的字段会回落成裸字段名（wechat_remove_message 这种），过长时省略号收尾，
     完整名在整行的悬浮提示里（title = 表名｜字段中文名（表.列名））。 */
  flex: 0 0 132px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #303133;
}

.row-values {
  display: flex;
  align-items: baseline;
  /* 不换行：类型标签与值永远左右成对；值过长时在值自己那一格里折行，
     不再整块掉到标签下面（前面两张表能左右排开、这张表排不下，就是这个宽度问题） */
  flex-wrap: nowrap;
  gap: 6px;
  flex: 1;
  min-width: 0;
}

.row-kind {
  /* 固定占一列：mini 标签「本次写入」实占 ≈58px，给 64px */
  display: inline-flex;
  align-items: baseline;
  flex: 0 0 64px;
}

.side-value {
  min-width: 0;
  word-break: break-all;
}

/* 旧值红 + 划掉、新值绿 + 加粗：不只靠颜色区分，色弱也读得懂 */
.side-old {
  color: #c45656;
}

.side-gone {
  text-decoration: line-through;
}

.side-new {
  color: #529b2e;
  font-weight: bold;
}

.row-arrow {
  color: #909399;
}
</style>
